import { create } from 'zustand';

import {
  AvailableMenus,
  AvailableTabs,
  TransactionGroupItem,
} from 'types/cash';

interface TransactionState {
  transactionGroups: TransactionGroupItem[];
  balances: {
    opening_balance: number;
    closing_balance: number;
  };
  transactionsFetching: boolean;
  setTransactionGroups: (groups: TransactionGroupItem[]) => void;
  setBalances: (balances: {
    opening_balance: number;
    closing_balance: number;
  }) => void;
  setTransactionsFetching: (value: boolean) => void;
  getTransactionGroupsByView: (
    menu: AvailableMenus,
    tab: AvailableTabs
  ) => TransactionGroupItem[];
  getBalances: () => {
    opening_balance: number;
    closing_balance: number;
  };
  removeTransactionGroup: (transactionGroupId: string) => void;
}

// Check if action type indicates rejection (includes REJECTED or AI_COULD_NOT_SUGGEST)
const isRejectionType = (actionType: string | undefined): boolean => {
  if (!actionType) return false;
  return (
    actionType.includes('REJECTED') || actionType === 'AI_COULD_NOT_SUGGEST'
  );
};

export const useTransactionStore = create<TransactionState>((set, get) => ({
  transactionsFetching: false,
  transactionGroups: [],
  balances: {
    opening_balance: 0,
    closing_balance: 0,
  },

  setTransactionGroups: (transactionGroups) => set({ transactionGroups }),
  setBalances: (balances) => set({ balances }),
  setTransactionsFetching: (value) => set({ transactionsFetching: value }),
  removeTransactionGroup: (transactionGroupId) =>
    set((state) => ({
      transactionGroups: state.transactionGroups.filter(
        (g) => g.transaction_group_id !== transactionGroupId
      ),
    })),

  getTransactionGroupsByView: (menu, tab) => {
    const { transactionGroups } = get();

    // Pending = unprocessed transactions (no action yet)
    if (menu === AvailableMenus.pending) {
      return transactionGroups.filter(
        (g) =>
          g.action === null ||
          g.transactions.some((t) => t.status !== 'PROCESSED')
      );
    }

    // Filter to processed groups only
    const processedGroups = transactionGroups.filter(
      (g) =>
        g.action !== null &&
        g.transactions.every((t) => t.status === 'PROCESSED')
    );

    if (menu === AvailableMenus.for_review) {
      // Separate rejected transactions from suggestions
      const rejectedGroups = processedGroups.filter((g) =>
        isRejectionType(g.action?.type)
      );
      const suggestionGroups = processedGroups.filter(
        (g) => !isRejectionType(g.action?.type)
      );

      if (tab === AvailableTabs.reconcile) {
        return suggestionGroups.filter((g) => g.action?.type === 'RECONCILE');
      }

      if (tab === AvailableTabs.match) {
        return suggestionGroups.filter((g) => g.action?.type === 'MATCH');
      }

      if (tab === AvailableTabs.categorize) {
        return suggestionGroups.filter((g) => g.action?.type === 'CATEGORIZE');
      }

      if (tab === AvailableTabs.no_match) {
        return rejectedGroups;
      }
    }

    return transactionGroups;
  },

  getBalances: () => ({ ...get().balances }),
}));
